import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, TrendingUp, Medal, Trash2, ShieldAlert, Swords, History, User, Info, Lock, Globe, Calendar } from 'lucide-react';
import Logo from '@/components/Logo';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RankingDetail() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerMatches, setPlayerMatches] = useState([]);
  const [isPlayerDialogOpen, setIsPlayerDialogOpen] = useState(false);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);

  useEffect(() => {
    loadRankingDetail();
  }, [listId]);

  const loadRankingDetail = async () => {
    try {
      // Try to get current user (may fail if not authenticated)
      let userData = null;
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        userData = userRes.data;
        setCurrentUser(userData);
        setIsAuthenticated(true);
      } catch (e) {
        setCurrentUser(null);
        setIsAuthenticated(false);
      }

      // Get online users
      try {
        const onlineRes = await axios.get(`${API}/users/online-ids`, { withCredentials: true });
        setOnlineUserIds(onlineRes.data.online_user_ids || []);
      } catch (e) {
        setOnlineUserIds([]);
      }

      const [listRes, allMatchesRes] = await Promise.all([
        axios.get(`${API}/ranking-lists/${listId}`, { withCredentials: true }),
        axios.get(`${API}/ranking-lists/${listId}/matches`, { withCredentials: true })
      ]);
      setList(listRes.data);
      setRankings(listRes.data.rankings || []);
      setAllMatches(allMatchesRes.data);
    } catch (error) {
      console.error('Ranking detail error:', error.response?.data || error.message);
      toast.error('Failed to load ranking details');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayerClick = async (ranking) => {
    try {
      const response = await axios.get(`${API}/ranking-lists/${listId}/player/${ranking.user_id}/matches`, { withCredentials: true });
      setSelectedPlayer(ranking);
      setPlayerMatches(response.data);
      setIsPlayerDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load player matches');
    }
  };

  const handleChallenge = async (opponentUserId, opponentName) => {
    try {
      // Get opponent's email
      const emailRes = await axios.get(`${API}/users/${opponentUserId}/email`, { withCredentials: true });
      
      // Send invite
      await axios.post(
        `${API}/invites`,
        {
          receiver_email: emailRes.data.email,
          list_id: listId
        },
        { withCredentials: true }
      );
      
      toast.success(`Challenge sent to ${opponentName}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send challenge');
    }
  };

  const handleDeleteMatch = async (matchId, matchInfo) => {
    try {
      await axios.delete(`${API}/admin/matches/${matchId}`, { withCredentials: true });
      toast.success('Match deleted and ELO recalculated!');
      loadRankingDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete match');
    }
  };

  const getMedalColor = (rank) => {
    if (rank === 1) return 'text-yellow-400';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-orange-400';
    return 'text-gray-600';
  };

  const getRankBorderColor = (rank) => {
    if (rank === 1) return 'border-yellow-400/50';
    if (rank === 2) return 'border-gray-400/50';
    if (rank === 3) return 'border-orange-400/50';
    return 'border-[#27272A]';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <Button 
            onClick={() => navigate('/ranking-lists')}
            variant="ghost"
            className="text-white hover:bg-white/10 text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            Back to Lists
          </Button>
          <Logo size="lg" />
        </div>

        <div className="mb-4 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Trophy className="w-8 h-8 sm:w-12 sm:h-12 text-[#007AFF]" />
                <h1 className="text-xl sm:text-3xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {list?.name}
                </h1>
                {list?.is_locked && (
                  <span className="flex items-center gap-1 text-xs sm:text-sm bg-[#FF3B30]/20 text-[#FF3B30] px-2 sm:px-3 py-1 rounded-full">
                    <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                    Locked
                  </span>
                )}
                {list?.is_public && (
                  <span className="flex items-center gap-1 text-xs sm:text-sm bg-[#007AFF]/20 text-[#007AFF] px-2 sm:px-3 py-1 rounded-full">
                    <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                    Public
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-[#007AFF]/20 text-[#007AFF] rounded-full text-xs sm:text-sm">
                  {list?.necessary_wins ? `${list.necessary_wins} wins needed` : list?.match_format}
                </Badge>
                {list?.ends_at && (
                  <Badge className="bg-[#27272A] text-gray-400 rounded-full text-xs sm:text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Until {new Date(list.ends_at).toLocaleDateString()}
                  </Badge>
                )}
              </div>
            </div>
            {/* Info button - always visible */}
            <Button
              data-testid="info-btn"
              onClick={() => setIsInfoDialogOpen(true)}
              className="bg-[#111111] hover:bg-[#1A1A1A] border border-[#27272A] text-white rounded-full"
            >
              <Info className="w-5 h-5 mr-2" />
              Info
            </Button>
          </div>

          {list?.is_locked && (
            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-[#FF3B30]">
                <Lock className="w-5 h-5" />
                <span className="font-semibold">This ranking list has ended</span>
              </div>
              <p className="text-gray-400 text-sm mt-1">No new matches can be played. Final rankings are shown below.</p>
            </div>
          )}
        </div>

        <Tabs defaultValue="leaderboard" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#111111]">
            <TabsTrigger value="leaderboard" data-testid="leaderboard-tab">
              <Trophy className="w-4 h-4 mr-2" />
              Leaderboard
            </TabsTrigger>
            <TabsTrigger value="matches" data-testid="matches-tab">
              <History className="w-4 h-4 mr-2" />
              Match History ({allMatches.length})
            </TabsTrigger>
          </TabsList>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card className="bg-[#111111] border-[#27272A]">
              <CardContent className="p-6">
                {rankings.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No players in this ranking list yet</p>
                    <p className="text-sm text-gray-500 mt-2">Be the first to join!</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="leaderboard">
                    {rankings.map((ranking, index) => (
                      <div 
                        key={ranking.player_id}
                        className={`bg-[#0A0A0A] border ${getRankBorderColor(index + 1)} rounded-xl p-4 flex items-center justify-between hover:border-[#007AFF]/50 transition-all cursor-pointer`}
                        data-testid="ranking-row"
                        onClick={() => handlePlayerClick(ranking)}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-12 text-center">
                            {index < 3 ? (
                              <Medal className={`w-8 h-8 ${getMedalColor(index + 1)}`} />
                            ) : (
                              <span className="text-2xl font-bold text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>
                                {index + 1}
                              </span>
                            )}
                          </div>
                          {/* Club Logo */}
                          <div className="w-10 h-10 flex-shrink-0">
                            {ranking.club_logo_url ? (
                              <img src={ranking.club_logo_url} alt={ranking.user_club} className="w-10 h-10 object-contain rounded" />
                            ) : (
                              <div className="w-10 h-10 bg-[#27272A] rounded flex items-center justify-center text-gray-500 text-xs">N/A</div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-bold text-lg hover:text-[#007AFF] transition-colors">
                                {ranking.user_nickname || ranking.user_name}
                              </p>
                              {ranking.user_nickname && (
                                <span className="text-sm text-gray-500">({ranking.user_name})</span>
                              )}
                              {/* Online indicator */}
                              {onlineUserIds.includes(ranking.user_id) ? (
                                <span className="flex items-center gap-1 text-xs text-green-400" title={t('online')}>
                                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                                  {t('online')}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs text-gray-500" title={t('offline')}>
                                  <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                                  {t('offline')}
                                </span>
                              )}
                            </div>
                            {ranking.user_club && (
                              <p className="text-sm text-gray-400">{ranking.user_club}</p>
                            )}
                            <div className="flex gap-4 mt-1">
                              <span className="text-xs text-gray-500">
                                {t('matches')}: {ranking.matches_played}
                              </span>
                              <span className="text-xs text-green-400">
                                {t('wins')}: {ranking.wins}
                              </span>
                              <span className="text-xs text-red-400">
                                {t('losses')}: {ranking.losses}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-4xl font-black text-[#007AFF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                              {ranking.elo_score}
                            </div>
                            <p className="text-xs text-gray-500">ELO</p>
                          </div>
                          
                          {/* Challenge button - only show for authenticated users, other players who are online, and if list is not locked */}
                          {isAuthenticated && ranking.user_id !== currentUser?.user_id && !list?.is_locked && (
                            onlineUserIds.includes(ranking.user_id) ? (
                              <Button
                                data-testid="challenge-player-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChallenge(ranking.user_id, ranking.user_nickname || ranking.user_name);
                                }}
                                className="bg-[#FF3B30] hover:bg-[#D6301E] text-white rounded-full font-bold"
                              >
                                <Swords className="w-4 h-4 mr-2" />
                                {t('challenge')}
                              </Button>
                            ) : (
                              <Button
                                disabled
                                className="bg-gray-600 text-gray-400 rounded-full font-bold cursor-not-allowed"
                                title={t('playerOffline')}
                              >
                                <Swords className="w-4 h-4 mr-2" />
                                {t('offline')}
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Match History Tab */}
          <TabsContent value="matches">
            <Card className="bg-[#111111] border-[#27272A]">
              <CardContent className="p-6">
                {allMatches.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No matches played yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allMatches.map(match => (
                      <div 
                        key={match.match_id}
                        className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 hover:border-[#007AFF]/50 transition-all"
                        data-testid="match-history-row"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="text-center min-w-[120px]">
                              <p className="text-sm text-gray-400 mb-1">{match.player1_nickname || match.player1_name}</p>
                              <p className="text-3xl font-black text-[#007AFF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                {match.player1_score}
                              </p>
                            </div>
                            <span className="text-gray-500 font-bold">VS</span>
                            <div className="text-center min-w-[120px]">
                              <p className="text-sm text-gray-400 mb-1">{match.player2_nickname || match.player2_name}</p>
                              <p className="text-3xl font-black text-[#FF3B30]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                {match.player2_score}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            {match.elo_change_p1 && (
                              <div className="flex gap-3 text-sm mb-1">
                                <span className={match.elo_change_p1 > 0 ? 'text-green-400' : 'text-red-400'}>
                                  {match.elo_change_p1 > 0 ? '+' : ''}{match.elo_change_p1}
                                </span>
                                <span className={match.elo_change_p2 > 0 ? 'text-green-400' : 'text-red-400'}>
                                  {match.elo_change_p2 > 0 ? '+' : ''}{match.elo_change_p2}
                                </span>
                              </div>
                            )}
                            <div className="text-xs text-gray-500 space-y-1">
                              {match.challenged_at && (
                                <p>{t('challenged')}: {new Date(match.challenged_at).toLocaleString()}</p>
                              )}
                              <p>{t('confirmed')}: {match.confirmed_at ? new Date(match.confirmed_at).toLocaleString() : 'N/A'}</p>
                            </div>
                            {isAdmin && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    data-testid="delete-match-btn"
                                    onClick={(e) => e.stopPropagation()}
                                    variant="outline"
                                    size="sm"
                                    className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full mt-2"
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Delete
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-white">Delete Match?</AlertDialogTitle>
                                    <AlertDialogDescription className="text-gray-400">
                                      This will delete the match and recalculate all ELO scores. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleDeleteMatch(match.match_id)}
                                      className="bg-[#FF3B30] hover:bg-[#D6301E]"
                                    >
                                      Delete Match
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Player Match History Dialog */}
        <Dialog open={isPlayerDialogOpen} onOpenChange={setIsPlayerDialogOpen}>
          <DialogContent className="bg-[#111111] border-[#27272A] max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                {selectedPlayer?.user_nickname || selectedPlayer?.user_name}&apos;S MATCH HISTORY
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                All matches in {list?.name} • ELO: {selectedPlayer?.elo_score}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {playerMatches.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No matches yet</p>
              ) : (
                playerMatches.map(match => (
                  <div 
                    key={match.match_id}
                    className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[100px]">
                          <p className="text-xs text-gray-400 mb-1">{match.player1_nickname || match.player1_name}</p>
                          <p className={`text-2xl font-black ${match.player1_id === selectedPlayer?.user_id ? 'text-[#007AFF]' : 'text-white'}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            {match.player1_score}
                          </p>
                        </div>
                        <span className="text-gray-500">VS</span>
                        <div className="text-center min-w-[100px]">
                          <p className="text-xs text-gray-400 mb-1">{match.player2_nickname || match.player2_name}</p>
                          <p className={`text-2xl font-black ${match.player2_id === selectedPlayer?.user_id ? 'text-[#007AFF]' : 'text-white'}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            {match.player2_score}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          (match.winner_id === selectedPlayer?.user_id) ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {match.winner_id === selectedPlayer?.user_id ? 'WIN' : 'LOSS'}
                        </p>
                        <p className={`text-sm ${
                          ((match.player1_id === selectedPlayer?.user_id && match.elo_change_p1 > 0) ||
                           (match.player2_id === selectedPlayer?.user_id && match.elo_change_p2 > 0))
                          ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {match.player1_id === selectedPlayer?.user_id 
                            ? `${match.elo_change_p1 > 0 ? '+' : ''}${match.elo_change_p1}`
                            : `${match.elo_change_p2 > 0 ? '+' : ''}${match.elo_change_p2}`
                          } ELO
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {match.confirmed_at ? new Date(match.confirmed_at).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Info Dialog */}
        <Dialog open={isInfoDialogOpen} onOpenChange={setIsInfoDialogOpen}>
          <DialogContent className="bg-[#111111] border-[#27272A] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-semibold flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                <Info className="w-6 h-6 text-[#007AFF]" />
                {list?.name}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Ranking List Information
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Necessary Wins</p>
                <p className="text-white font-bold">{list?.necessary_wins || list?.match_format}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-2">Description</p>
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                  <p className="text-white whitespace-pre-wrap">{list?.description || 'No description provided for this ranking list.'}</p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default RankingDetail;